/**
 * "Add another chat" modal — generates a fresh Telegram invite link on open.
 * Same invite machinery as the pre-link TelegramLinkCard: the API returns a
 * deep link carrying a `{TELEGRAM_BOT_USERNAME}` placeholder that we fill
 * from `/v1/config`. Anyone who opens the link in Telegram and taps Start
 * gets their chat linked to this project's digests.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, MessageCircle, RotateCcw, Send, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { Spinner } from '@/components/spinner';
import { ApiError, summarizeApiError } from '@/lib/api';
import { useAutoCampaignInviteLink } from '@/features/auto-campaign/mutations';
import { useRuntimeConfig } from '@/features/auto-campaign/use-config';

const INVITE_TTL_SECONDS = 3600;

export interface AddChatDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function AddChatDialog({
  projectId,
  open,
  onOpenChange,
}: AddChatDialogProps): JSX.Element {
  const config = useRuntimeConfig();
  const invite = useAutoCampaignInviteLink(projectId);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);

  async function generate(): Promise<void> {
    try {
      const res = await invite.mutateAsync(INVITE_TTL_SECONDS);
      setInviteUrl(res.deep_link_short);
      setExpiresAt(res.expires_at);
      setNotConfigured(false);
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 503 &&
        err.code === 'TELEGRAM_LINK_TOKEN_SECRET_UNSET'
      ) {
        setNotConfigured(true);
        return;
      }
      const s = summarizeApiError(err, {
        fallbackTitle: 'Could not generate invite link',
      });
      toast.error(s.title, { description: s.description });
    }
  }

  // Fresh link every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setInviteUrl(null);
    setExpiresAt(null);
    void generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  const botUsername = config.data?.telegram_bot_username ?? null;
  const fullLink =
    inviteUrl && botUsername
      ? inviteUrl.replaceAll('{TELEGRAM_BOT_USERNAME}', botUsername)
      : null;
  // Deep links only work in a 1:1 chat — Telegram ignores ?start= when a bot
  // is added to a group. For groups the payload has to be sent as a command
  // inside the group itself, so surface it as copyable text.
  const groupCommand = inviteUrl
    ? `/start ${decodeURIComponent(inviteUrl.split('?start=')[1] ?? '')}`
    : null;

  async function copyGroupCommand(): Promise<void> {
    if (!groupCommand) return;
    try {
      await navigator.clipboard.writeText(groupCommand);
      toast.success('Command copied — paste it in your group');
    } catch {
      toast.error('Could not copy — please copy the command manually.');
    }
  }

  async function copyLink(): Promise<void> {
    if (!fullLink) return;
    try {
      await navigator.clipboard.writeText(fullLink);
      toast.success('Invite link copied');
    } catch {
      toast.error('Could not copy — please copy the link manually.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-primary" />
            Add another chat
          </DialogTitle>
          <DialogDescription>
            Open the link yourself (from another Telegram account or a group)
            or send it to a teammate. Whoever taps Start in Telegram gets this
            project's digests.
          </DialogDescription>
        </DialogHeader>

        {notConfigured ? (
          <Callout tone="warning" title="Telegram link tokens not configured">
            The <code className="font-mono text-xs">TELEGRAM_LINK_TOKEN_SECRET</code>{' '}
            env var isn't set on this deployment.
          </Callout>
        ) : invite.isPending && !fullLink ? (
          <div className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" />
            Generating invite link…
          </div>
        ) : fullLink ? (
          <div className="space-y-3">
            <p className="break-all rounded bg-muted/50 p-2 font-mono text-xs text-muted-foreground">
              {fullLink}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button asChild size="sm">
                <a href={fullLink} target="_blank" rel="noreferrer">
                  <Send className="h-3.5 w-3.5" />
                  Open in Telegram
                </a>
              </Button>
              <Button variant="outline" size="sm" onClick={copyLink}>
                <Copy className="h-3.5 w-3.5" />
                Copy link
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void generate()}
                disabled={invite.isPending}
              >
                {invite.isPending ? (
                  <Spinner className="mr-1 h-3.5 w-3.5" />
                ) : (
                  <RotateCcw className="h-3.5 w-3.5" />
                )}
                Regenerate
              </Button>
            </div>
            {expiresAt ? (
              <p className="text-xs text-muted-foreground">
                Link valid until {new Date(expiresAt).toLocaleTimeString()}.
              </p>
            ) : null}

            {groupCommand ? (
              <div className="space-y-2 rounded-md border border-border/60 bg-muted/30 p-3">
                <div className="flex items-center gap-1.5 text-sm font-medium">
                  <Users className="h-3.5 w-3.5" />
                  Deliver to a group instead
                </div>
                <ol className="ml-4 list-decimal space-y-1 text-xs text-muted-foreground">
                  <li>
                    Add{' '}
                    <span className="font-mono">
                      @{botUsername ?? 'your_bot'}
                    </span>{' '}
                    to the Telegram group.
                  </li>
                  <li>Paste this command in the group and send it.</li>
                </ol>
                <p className="break-all rounded bg-background/70 p-2 font-mono text-xs">
                  {groupCommand}
                </p>
                <Button variant="outline" size="sm" onClick={() => void copyGroupCommand()}>
                  <Copy className="h-3.5 w-3.5" />
                  Copy group command
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
